import type { IComponentController, IScope } from "angular";
import type {
    IConfigurationGroupMonitoring,
    IMonitoringNotificationSubscription,
    IPartnerUser,
    ISite,
} from "App/Modules/Models";
import { NotificationType } from "App/Modules/Models";
import type { Duration } from "moment";
import { duration } from "moment";

enum NotificationLevel {
    Critical,
    Warning,
    Information,
}

export default class EditIssueSubscriptionsComponentController implements IComponentController {
    public static $inject: string[] = ["$scope"];

    public configurationGroup: IConfigurationGroupMonitoring;
    public site: ISite;

    public subscriptions: IMonitoringNotificationSubscription[];

    public subscribersAssigned: boolean;
    public types: any[];

    public notificationTypes: any[] = [
        {
            type: NotificationType.SystemFailure,
            level: NotificationLevel.Critical,
            enabled: false,
        },
        {
            type: NotificationType.ConnectionFailure,
            level: NotificationLevel.Critical,
            enabled: false,
        },
        {
            type: NotificationType.VideoQualityFailure,
            level: NotificationLevel.Critical,
            enabled: false,
        },
        {
            type: NotificationType.Tampering,
            level: NotificationLevel.Critical,
            enabled: false,
        },
        {
            type: NotificationType.ConnectionDegraded,
            level: NotificationLevel.Warning,
            enabled: false,
        },
        {
            type: NotificationType.ResourcesFailure,
            level: NotificationLevel.Warning,
            enabled: false,
        },
    ];

    public subscribers: IPartnerUser[] = [];

    public existingSubscribers: string[];

    public disabled: boolean;

    public durations: Array<{ value: Duration; text: string }> = [
        { value: duration(1, "seconds"), text: "Gui.Common.Immediate" },
        {
            value: duration(5, "minutes"),
            text: duration(5, "minutes").humanize(),
        },
        {
            value: duration(15, "minutes"),
            text: duration(15, "minutes").humanize(),
        },
        {
            value: duration(30, "minutes"),
            text: duration(30, "minutes").humanize(),
        },
        { value: duration(1, "hours"), text: duration(1, "hours").humanize() },
        { value: duration(23, "hours").add(59, "minutes").add(59, "seconds"), text: duration(1, "days").humanize() },
    ];
    public currentDuration: { value: Duration; text: string };

    constructor(private readonly $scope: IScope) {}

    public $onInit() {
        const unsub = this.$scope.$watch(
            "$ctrl.subscriptions",
            (subscriptions: IMonitoringNotificationSubscription[]) => {
                if (!subscriptions) {
                    return;
                }

                this.notificationTypes.forEach((notificationType) => {
                    notificationType.selected = subscriptions.some(
                        (sub) => sub.notificationType === notificationType.type,
                    );
                });

                subscriptions.forEach((subscription) => {
                    const subscriberExists = this.subscribers.some(
                        (subscriber) => subscriber.id === subscription.partnerUser.id,
                    );

                    if (!subscriberExists) {
                        this.subscribers.push(subscription.partnerUser);
                    }
                });

                this.existingSubscribers = this.subscribers.map((x) => x.id);
                unsub();
            },
        );

        this.currentDuration = {
            text: duration(this.configurationGroup.timeUntilNotification).humanize(),
            value: duration(this.configurationGroup.timeUntilNotification),
        };

        this.$scope.$watch("$ctrl.currentDuration.value", (newValue: Duration, oldValue: Duration) => {
            if (newValue.asMilliseconds() !== oldValue.asMilliseconds()) {
                this.configurationGroup.timeUntilNotification = (newValue as any).format("hh:mm:ss", { trim: false });
            }
        });
    }

    public addSubscriber(user: IPartnerUser) {
        if (this.subscribers.some((subs) => subs.id === user.id)) {
            return;
        }

        this.subscribers.push(user);
        this.existingSubscribers.push(user.id);

        this.onInput();
    }

    public removeSubscriber(index: number) {
        this.subscribers.splice(index, 1);

        this.onInput();
    }

    public onInput() {
        this.subscriptions = [];
        this.selectedNotifications().forEach((type) => {
            this.subscribers.forEach((subscriber) => {
                this.subscriptions.push({
                    notificationType: type,
                    partnerUser: subscriber,
                } as IMonitoringNotificationSubscription);
            });
        });

        this.subscribersAssigned =
            !this.areNotificationsSelected() || (this.areNotificationsSelected() && this.subscribers.length > 0);
    }

    public areNotificationsSelected(): boolean {
        return this.notificationTypes.some((type) => type.selected);
    }

    public selectedNotifications(): number[] {
        return this.notificationTypes.filter((type) => type.selected).map((type) => type.type);
    }

    public typesCritical(): any[] {
        return this.notificationTypes.filter((type) => type.level === NotificationLevel.Critical);
    }

    public typesWarning(): any[] {
        return this.notificationTypes.filter((type) => type.level === NotificationLevel.Warning);
    }

    public typesInfo(): any[] {
        return this.notificationTypes.filter((type) => type.level === NotificationLevel.Information);
    }

    public toggleShowMore(event: any): void {
        event.target.closest(".show-more").classList.toggle("show-more--active");
    }
}
